import { GetProductOffersResponse } from '@iwoca/lapi-client/edge';
import { RootState } from '@iwProvider/configureStore';
import _ from 'lodash';

import {
    APPLICATION_STATUS_OPEN,
    hasApplicationWithProductType
} from '../../../../api/lendingAPI/ApplicationSelector';
import { TProductType } from '../../../dashboard/Dashboard.types';
import * as uploadDocsHelper from '../../../modals/uploadDocumentsModal/UploadDocumentsModalHelper';
import { FLEXI_LOAN_PRODUCT_TYPE } from '../../accountsSelector';

type TProductOffers = NonNullable<GetProductOffersResponse['data']>['products'];
type TDataRequirements = TProductOffers[number]['data_requirements'];
type TResolutionMethods = NonNullable<
    TDataRequirements[number]['resolution_methods']
>;

export const getBankStatementsDataRequirement = (state: RootState) => {
    const suggestedProduct = getSuggestedProduct(state);
    if (!suggestedProduct) return undefined;

    return getDocumentRequirementsUsingProductOffers(suggestedProduct).find(
        ({ type }) =>
            ['bank_statement', 'kontoauszuege_hochladen'].includes(type!)
    );
};

export const isFlexiLoanBankStatementRequired = (state: RootState) => {
    const suggestedProduct = getSuggestedProduct(state);
    if (!suggestedProduct) return false;

    const bankStatementRequirement = _.find(
        suggestedProduct.data_requirements,
        {
            type: 'bank_statement'
        }
    );

    return bankStatementRequirement?.status === 'required';
};

export const getSuggestedProduct = (state: RootState) => {
    const productOffersState = state?.api?.product_offers?.data?.products;
    return _.find(productOffersState, {
        is_suggested: true
    });
};

export const getDocumentRequirements = (state: RootState) => {
    const suggestedProduct: TProductOffers[number] = getSuggestedProduct(state);

    if (!suggestedProduct) return [];

    const UK_SUPPORTED_DOCUMENTS = [
        'company_accounts',
        'vat_return',
        'self_assessment_tax_return',
        'management_accounts',
        'aged_debtor_report'
    ];
    const DE_SUPPORTED_DOCUMENTS = [
        'kontoauszuege_hochladen',
        'bwa_hochladen',
        'jaehrliche_bwa_susa_hochladen',
        'bwa_susa_hochladen'
    ];

    const documentRequirements = suggestedProduct.data_requirements.filter(
        ({ type, is_optional }) =>
            _.includes(
                [...UK_SUPPORTED_DOCUMENTS, ...DE_SUPPORTED_DOCUMENTS],
                type
            ) && !is_optional
    );

    const UK_DOCUMENT_TYPE_MAPPING = {
        vat_return: uploadDocsHelper.VAT_RETURNS_DOC_TYPE,
        company_accounts: uploadDocsHelper.BUSINESS_ACCOUNTS_DOC_TYPE,
        self_assessment_tax_return:
            uploadDocsHelper.SELF_ASSESSMENT_TAX_RETURN_DOC_TYPE,
        management_accounts: uploadDocsHelper.MANAGEMENT_ACCOUNTS_DOC_TYPE,
        aged_debtor_report: uploadDocsHelper.AGED_DEBTOR_REPORT_DOC_TYPE
    };

    const DE_DOCUMENT_TYPE_MAPPING = {
        bwa_susa_hochladen:
            uploadDocsHelper.MANAGEMENT_AND_BALANCE_ACCOUNTS_DOC_TYPE,
        jaehrliche_bwa_susa_hochladen:
            uploadDocsHelper.YEAR_END_MANAGEMENT_ACCOUNTS_DOC_TYPE,
        bwa_hochladen: uploadDocsHelper.MANAGEMENT_ACCOUNTS_DOC_TYPE,
        kontoauszuege_hochladen: uploadDocsHelper.BANK_STATMENT_DOC_TYPE
    };

    const documentTypeMapping = {
        ...UK_DOCUMENT_TYPE_MAPPING,
        ...DE_DOCUMENT_TYPE_MAPPING
    };

    return documentRequirements.map(
        ({ status, type, quantifier, resolution_methods }) => {
            return {
                status,
                type,
                quantifier,
                uploadModalDocType:
                    documentTypeMapping[
                        type! as keyof typeof documentTypeMapping
                    ],
                resolution_methods,
                hasItems: hasDocumentRequirementEntries(
                    documentRequirements,
                    type!,
                    'items'
                ),
                hasError: hasDocumentRequirementEntries(
                    documentRequirements,
                    type!,
                    'errors'
                )
            };
        }
    );
};

export const getDocumentRequirementsUsingProductOffers = (
    suggestedProduct: TProductOffers[number]
) => {
    const UK_SUPPORTED_DOCUMENTS = [
        'company_accounts',
        'vat_return',
        'self_assessment_tax_return',
        'management_accounts',
        'aged_debtor_report',
        'link_open_banking',
        'bank_statement'
    ];
    const DE_SUPPORTED_DOCUMENTS = [
        'kontoauszuege_hochladen',
        'bwa_hochladen',
        'jaehrliche_bwa_susa_hochladen',
        'bwa_susa_hochladen'
    ];

    const documentRequirements = suggestedProduct.data_requirements.filter(
        ({ type, is_optional }) =>
            _.includes(
                [...UK_SUPPORTED_DOCUMENTS, ...DE_SUPPORTED_DOCUMENTS],
                type
            ) && !is_optional
    );

    const UK_DOCUMENT_TYPE_MAPPING = {
        vat_return: uploadDocsHelper.VAT_RETURNS_DOC_TYPE,
        company_accounts: uploadDocsHelper.BUSINESS_ACCOUNTS_DOC_TYPE,
        self_assessment_tax_return:
            uploadDocsHelper.SELF_ASSESSMENT_TAX_RETURN_DOC_TYPE,
        management_accounts: uploadDocsHelper.MANAGEMENT_ACCOUNTS_DOC_TYPE,
        aged_debtor_report: uploadDocsHelper.AGED_DEBTOR_REPORT_DOC_TYPE,
        bank_statement: uploadDocsHelper.BANK_STATMENT_DOC_TYPE
    };

    const DE_DOCUMENT_TYPE_MAPPING = {
        bwa_susa_hochladen:
            uploadDocsHelper.MANAGEMENT_AND_BALANCE_ACCOUNTS_DOC_TYPE,
        jaehrliche_bwa_susa_hochladen:
            uploadDocsHelper.YEAR_END_MANAGEMENT_ACCOUNTS_DOC_TYPE,
        bwa_hochladen: uploadDocsHelper.MANAGEMENT_ACCOUNTS_DOC_TYPE,
        kontoauszuege_hochladen: uploadDocsHelper.BANK_STATMENT_DOC_TYPE
    };

    const documentTypeMapping = {
        ...UK_DOCUMENT_TYPE_MAPPING,
        ...DE_DOCUMENT_TYPE_MAPPING
    };

    return documentRequirements.map(
        ({ status, type, quantifier, resolution_methods }) => {
            return {
                status,
                type,
                quantifier,
                uploadModalDocType:
                    documentTypeMapping[
                        type! as keyof typeof documentTypeMapping
                    ],
                resolution_methods,
                hasItems: hasDocumentRequirementEntries(
                    documentRequirements,
                    type!,
                    'items'
                ),
                hasError: hasDocumentRequirementEntries(
                    documentRequirements,
                    type!,
                    'errors'
                )
            };
        }
    );
};

const hasDocumentRequirementEntries = (
    documentRequirements: TDataRequirements,
    requirementName: string,
    entryName: 'errors' | 'items'
) => {
    const documentRequirement = _.find(documentRequirements, {
        type: requirementName
    });

    const resolutionMethods: TResolutionMethods =
        documentRequirement?.resolution_methods ?? [];

    return (
        resolutionMethods.reduce((accumulator, method) => {
            // @ts-expect-error
            accumulator.push(...method[entryName]);

            return accumulator;
        }, []).length > 0
    );
};

export const hasOpenApplication = (
    state: RootState,
    productType: TProductType = FLEXI_LOAN_PRODUCT_TYPE
) =>
    state?.api?.application?.data?.status === APPLICATION_STATUS_OPEN &&
    hasApplicationWithProductType(state, productType);
